import * as React from 'react';
import {
  Text,
  Box,
  Columns,
  Column,
  Stack,
  Strong,
  CheckboxStandalone,
  Alert,
  Inline,
  Loader,
} from 'braid-design-system';
import { observer } from 'mobx-react';
import BulkActions from './BulkActions';

import type { IJobApplicationStore } from '../../../stores/JobApplicationStore';
import SortOrderDropdown from '../../app/SortOrderDropdown';
import * as styles from './ActionToolbar.css';
import { SearchResultSummary } from './SearchResultSummary';
import TextButton from 'src/components/common/TextButton';
import { FilterAction } from './FilterAction/FilterAction';
import { AddCandidateAction } from './AddCandidateAction/AddCandidateAction';
import translations from './.vocab/';
import { useTranslations } from '@vocab/react';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';
import { BULK_MOVE_LIMIT, PER_PAGE } from 'src/constants/numPerPage';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';

export interface ActionToolbarProps {
  jobApplicationStore: IJobApplicationStore;
  isMobile?: boolean;
  isJobTabsSticky: boolean;
  showOldJobIntercom: boolean;
  checkboxProps: {
    size?: 'standard' | 'small';
    isDisabled: boolean;
    isChecked: boolean;
    onChange: () => void;
  };
  applicationCardStateButton: JSX.Element | null;
}

// We are loading all job applications from the Graph with secondary select all (a.k.a clickSelectAllJobApplicationsInBucket)
// For this reason, Jobs with a large number of applications in the current bucket may take a few seconds to load
// From what we observed, anything higher than 500 records to load take a time long enough to be noticed by users (internet speed may cause result to vary)
const LOAD_ALL_THRESHOLD_FOR_LOADER = 500;

const ActionToolbar: React.FC<ActionToolbarProps> = ({
  jobApplicationStore,
  isMobile = false,
  isJobTabsSticky,
  showOldJobIntercom,
  checkboxProps,
  applicationCardStateButton,
}) => {
  const { t } = useTranslations(translations);
  const [isLoadingAllJobApplications, setIsLoadingAllJobApplications] =
    React.useState(false);
  const { getLabelFromBucket } = useBucketLabel();
  const { selectedCount, hasSearchText, currentTotalOfJobApplications } =
    jobApplicationStore;

  const bucketLabel = getLabelFromBucket(jobApplicationStore.bucket);

  const selectAllLabel = hasSearchText
    ? t('All results')
    : t('Bucket', { bucket: bucketLabel });

  if (isMobile) {
    return <SearchResultSummary jobApplicationStore={jobApplicationStore} />;
  }

  const showSearchResultSummary =
    jobApplicationStore?.hasSearchText ||
    jobApplicationStore?.filtersStore?.hasSelectedFilter;
  const showSelectedCount = selectedCount > 0;
  const isBulkActionEnabled = selectedCount > 0 && !isLoadingAllJobApplications;

  return (
    <Box
      paddingX="medium"
      paddingTop="medium"
      paddingBottom="small"
      className={[
        styles.actionWrapper,
        { [styles.stickyBelowTab]: isJobTabsSticky },
      ]}
      data-cy="action-toolbar"
      background="surface"
    >
      <Stack space="medium">
        {!showOldJobIntercom &&
          (showSearchResultSummary || showSelectedCount) && (
            <>
              {showSearchResultSummary && (
                <SearchResultSummary
                  jobApplicationStore={jobApplicationStore}
                />
              )}
              {showSelectedCount && (
                <Inline space="none">
                  <Box paddingRight="medium">
                    <Text>
                      {t('jobApplicationCount application/s are selected.', {
                        jobApplicationCount: selectedCount,
                        Strong: (children) => (
                          <Strong key={`${children}`}>{children}</Strong>
                        ),
                      })}
                    </Text>
                  </Box>
                  {currentTotalOfJobApplications > PER_PAGE && (
                    <SelectAllButton
                      currentTotalOfJobApplications={
                        currentTotalOfJobApplications
                      }
                      selectedCount={selectedCount}
                      jobApplicationStore={jobApplicationStore}
                      bucketLabel={bucketLabel}
                      isLoadingAllJobApplications={isLoadingAllJobApplications}
                      setIsLoadingAllJobApplications={
                        setIsLoadingAllJobApplications
                      }
                    />
                  )}
                </Inline>
              )}
            </>
          )}
        {selectedCount > BULK_MOVE_LIMIT && (
          <Alert>
            <Text>
              {t(
                'Remember, you can only move bulkMoveLimit applications to a different status folder at a time. There is no limit for emailing candidates.',
                { bulkMoveLimit: BULK_MOVE_LIMIT },
              )}
            </Text>
          </Alert>
        )}
        <Columns space="small" alignY="center">
          <Column>
            <CheckboxStandalone
              id="select-all"
              disabled={checkboxProps.isDisabled}
              aria-label={selectAllLabel}
              checked={checkboxProps.isChecked}
              onChange={checkboxProps.onChange}
              size={checkboxProps.size || 'standard'}
            />
          </Column>

          {applicationCardStateButton && (
            <Column width="content">{applicationCardStateButton}</Column>
          )}

          <Column width="content">
            <Box display="flex" flexWrap="wrap" justifyContent="flexEnd">
              <BulkActions
                jobApplicationStore={jobApplicationStore}
                disabled={!isBulkActionEnabled}
              />
            </Box>
          </Column>
          <Column width="content">
            <AddCandidateAction />
          </Column>
          <Column width="content">
            <Box display="flex" justifyContent="flexEnd" flexGrow={1}>
              <FilterAction />
            </Box>
          </Column>
          <Column width="content">
            <Box display="flex" justifyContent="flexEnd">
              <SortOrderDropdown jobApplicationStore={jobApplicationStore} />
            </Box>
          </Column>
        </Columns>
      </Stack>
    </Box>
  );
};

const SelectAllButton: React.FC<{
  currentTotalOfJobApplications: number;
  selectedCount: number;
  jobApplicationStore: IJobApplicationStore;
  bucketLabel: string;
  isLoadingAllJobApplications: boolean;
  setIsLoadingAllJobApplications: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
  currentTotalOfJobApplications,
  selectedCount,
  jobApplicationStore,
  bucketLabel,
  isLoadingAllJobApplications,
  setIsLoadingAllJobApplications,
}) => {
  const { t } = useTranslations(translations);
  const { language } = useAppConfig();

  if (isLoadingAllJobApplications) {
    return (
      <Box data-cy="secondary-select-all-loader">
        <Loader />
      </Box>
    );
  }

  if (currentTotalOfJobApplications !== selectedCount) {
    return (
      <Text>
        <TextButton
          onClick={() => {
            if (currentTotalOfJobApplications > LOAD_ALL_THRESHOLD_FOR_LOADER) {
              setIsLoadingAllJobApplications(true);
            }
            jobApplicationStore.clickSelectAllJobApplicationsInBucket({
              onCompleted: () => setIsLoadingAllJobApplications(false),
              language,
            });
          }}
        >
          {jobApplicationStore.hasSearchText
            ? t('Select all totalJobApplicationCount applications', {
                totalJobApplicationCount: currentTotalOfJobApplications,
              })
            : t(
                'Select all totalJobApplicationCount applications in bucketLabel',
                {
                  bucketLabel,
                  totalJobApplicationCount: currentTotalOfJobApplications,
                },
              )}
        </TextButton>
      </Text>
    );
  }

  return (
    <Text>
      <TextButton
        onClick={() =>
          jobApplicationStore.clickClearAllSelected({
            tracking: {
              actionOrigin: 'Candidate Listing - Clear Select Button',
            },
          })
        }
      >
        {t('Clear selection')}
      </TextButton>
    </Text>
  );
};

export default observer(ActionToolbar);
